import * as React from 'react';
import { cn } from '@/lib/utils';

const GuideBlock = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-guide-block',
      // Estilos base
      'flex max-w-full flex-col gap-4',
      // Estilos navegação
      '[&_.c-guide-block-nav]:shrink-0 [&_.c-guide-block-nav]:lg:w-[236px]',
      // Estilos artigo
      '[&_.c-guide-block-article]:grow',
      className
    )}
    {...props}
  />
));
GuideBlock.displayName = 'GuideBlock';

const GuideBlockHeader = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-guide-block-header',
      // Estilos base
      'flex flex-wrap items-center justify-between gap-4',
      className
    )}
    {...props}
  />
));
GuideBlockHeader.displayName = 'GuideBlockHeader';

const GuideBlockHeaderContent = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-guide-block-header-content',
      // Estilos base
      '',
      className
    )}
    {...props}
  />
));
GuideBlockHeaderContent.displayName = 'GuideBlockHeaderContent';

const GuideBlockTitle = React.forwardRef(({ className, ...props }, ref) => (
  <h2
    ref={ref}
    className={cn(
      'c-guide-block-title',
      // Estilos base
      'mb-0 text-2xl font-bold leading-normal text-primary-dark',
      className
    )}
    {...props}
  />
));
GuideBlockTitle.displayName = 'GuideBlockTitle';

const GuideBlockBtn = React.forwardRef(({ className, ...props }, ref) => (
  <button
    ref={ref}
    className={cn(
      'c-guide-block-btn',
      // Estilos base
      'inline-flex items-center gap-2',
      'cursor-pointer text-sm font-light text-primary',
      // Estilos ícone
      '[&>svg]:w-6 [&>svg]:shrink-0 [&>svg]:stroke-[1px]',
      className
    )}
    {...props}
  />
));
GuideBlockBtn.displayName = 'GuideBlockBtn';

const GuideBlockContent = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-guide-block-content',
      // Estilos base
      'flex flex-col gap-[42px]',
      // Estilos responsivos
      'lg:flex-row lg:gap-[84px]',
      className
    )}
    {...props}
  />
));
GuideBlockContent.displayName = 'GuideBlockContent';

const GuideBlockPlayer = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-guide-block-player',
      // Estilos base
      '[&_iframe]:aspect-[16/9]',
      className
    )}
    {...props}
  />
));
GuideBlockPlayer.displayName = 'GuideBlockPlayer';

const GuideBlockNav = React.forwardRef(({ className, ...props }, ref) => (
  <ul
    ref={ref}
    className={cn(
      'c-guide-block-nav',
      // Estilos base
      'flex flex-col gap-3',
      className
    )}
    {...props}
  />
));
GuideBlockNav.displayName = 'GuideBlockNav';

const GuideBlockNavItem = React.forwardRef(({ className, isActive, isSuccess, ...props }, ref) => (
  <li
    ref={ref}
    className={cn(
      'c-guide-block-nav-item',
      // Estilos base
      'flex cursor-default items-center gap-2 p-4 px-3',
      'rounded-[10px] border border-solid border-neutral-lightest bg-white bg-clip-padding',
      'shadow-card',
      // Variantes
      isActive && 'bg-bright',
      isSuccess && 'bg-success',
      className
    )}
    {...props}
  />
));
GuideBlockNavItem.displayName = 'GuideBlockNavItem';

const GuideBlockNavIcon = React.forwardRef(({ className, isActive, isSuccess, ...props }, ref) => (
  <span
    ref={ref}
    className={cn(
      'c-guide-block-nav-icon',
      // Estilos base
      'inline-flex h-6 w-6 shrink-0 items-center justify-center',
      'rounded-[50px] bg-bright text-primary',
      // Estilos ícone
      '[&>img]:w-4 [&>svg]:w-4',
      // Variantes
      isActive && 'bg-white',
      isSuccess && 'bg-white text-success',
      className
    )}
    {...props}
  />
));
GuideBlockNavIcon.displayName = 'GuideBlockNavIcon';

const GuideBlockNavTitle = React.forwardRef(({ className, isSuccess, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn(
      'c-guide-block-nav-title',
      // Estilos base
      'mb-0 grow truncate',
      'text-xs font-bold text-primary-dark',
      isSuccess && 'text-white',
      className
    )}
    {...props}
  />
));
GuideBlockNavTitle.displayName = 'GuideBlockNavTitle';

const GuideBlockNavCheck = React.forwardRef(({ className, isSuccess, checked, ...props }, ref) => {
  return (
    <div className={cn('relative inline-flex items-center justify-center', className)}>
      <input
        ref={ref}
        type="checkbox"
        className="sr-only"
        checked={checked}
        {...props}
      />

      <span
        className={cn(
          'c-study-guide-card-check',
          // Estilos base do checkbox visual
          'inline-flex items-center justify-center',
          'm-0 size-4 shrink-0 appearance-none',
          'rounded-full border border-solid border-neutral-light bg-white',
          'transition-all duration-200 ease-in-out',
          'cursor-pointer bg-center outline-none',
          // Estilos de estado
          checked && 'border-success bg-success',
          isSuccess && 'border-white bg-white'
        )}
      >
        {checked && (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="white"
            strokeWidth="4"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={`h-3 w-3 ${isSuccess ? 'stroke-success' : 'stroke-white'}`}
          >
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
        )}
      </span>
    </div>
  );
});
GuideBlockNavCheck.displayName = 'GuideBlockNavCheck';

const GuideBlockArticle = React.forwardRef(({ className, ...props }, ref) => (
  <article
    ref={ref}
    className={cn(
      'c-guide-block-article',
      // Estilos base
      'flex flex-col gap-5 p-8',
      'rounded-[10px] bg-white',
      'shadow-card-hover',
      className
    )}
    {...props}
  />
));
GuideBlockArticle.displayName = 'GuideBlockArticle';

const GuideBlockArticleHat = React.forwardRef(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn(
      'c-guide-block-article-hat',
      // Estilos base
      'mb-0',
      'text-sm font-bold text-neutral',
      className
    )}
    {...props}
  />
));
GuideBlockArticleHat.displayName = 'GuideBlockArticleHat';

const GuideBlockArticleTitle = React.forwardRef(({ className, ...props }, ref) => (
  <h1
    ref={ref}
    className={cn(
      'c-guide-block-article-title',
      // Estilos base
      'mb-0',
      'text-2xl font-bold text-primary-dark',
      className
    )}
    {...props}
  />
));
GuideBlockArticleTitle.displayName = 'GuideBlockArticleTitle';

const GuideBlockArticleHeader = React.forwardRef(({ className, ...props }, ref) => (
  <header
    ref={ref}
    className={cn(
      'c-guide-block-article__title',
      // Estilos base
      '',
      className
    )}
    {...props}
  />
));
GuideBlockArticleHeader.displayName = 'GuideBlockArticleHeader';

const GuideBlockArticleContent = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-guide-block-article__content',
      // Estilos para todos os filhos diretos
      '[&>*]:text-neutral-darker',
      // Estilos para p e ul filhos diretos
      '[&>p]:mb-8',
      '[&>ul]:mb-8',
      // Estilos para todas as imagens internas
      '[&_img]:w-[512px]',
      '[&_img]:rounded-[10px]',
      // Estilos para todas as listas internas
      '[&_ul]:list-disc',
      '[&_ul]:pl-5',
      className
    )}
    {...props}
  />
));
GuideBlockArticleContent.displayName = 'GuideBlockArticleContent';

const GuideBlockArticleFooter = React.forwardRef(({ className, ...props }, ref) => (
  <footer
    ref={ref}
    className={cn(
      'c-guide-block-article-footer',
      // Estilos de posicionamento
      'fixed bottom-8 right-8',
      // Estilos de visualização
      'rounded-[100px] bg-white',
      className
    )}
    {...props}
  />
));
GuideBlockArticleFooter.displayName = 'GuideBlockArticleFooter';

export {
  GuideBlock,
  GuideBlockHeader,
  GuideBlockHeaderContent,
  GuideBlockTitle,
  GuideBlockBtn,
  GuideBlockContent,
  GuideBlockPlayer,
  GuideBlockNav,
  GuideBlockNavItem,
  GuideBlockNavIcon,
  GuideBlockNavTitle,
  GuideBlockNavCheck,
  GuideBlockArticle,
  GuideBlockArticleHat,
  GuideBlockArticleTitle,
  GuideBlockArticleHeader,
  GuideBlockArticleContent,
  GuideBlockArticleFooter
};
